import { useEffect, useMemo, useRef, useState } from 'react';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import {
  isMorphTrack,
  loadBVHFiles,
  resolvePrimaryPositionTrackName,
} from '@cartwhl/3d/bvh';
import type { RotationOffsets } from '@cartwhl/common/types';

import { buildBvhUrlsIdentity } from '../lib/playerAnimationSourceIdentity';

export { buildBvhUrlsIdentity } from '../lib/playerAnimationSourceIdentity';

export interface UseBVHAnimationConfig {
  bvh?: BVH;
  bvhUrl?: string;
  rotationOffsets?: RotationOffsets;
}

export interface UseBVHAnimationsConfig {
  bvhUrls: string[];
  reloadKey?: string;
  rotationOffsets?: RotationOffsets;
}

export function normalizeBVHForPlayback(bvh: BVH): BVH {
  const rootPositionTrackName = resolvePrimaryPositionTrackName(bvh);
  const userData = (
    bvh.clip as unknown as { userData?: Record<string, unknown> }
  ).userData;
  const morphJoints = userData?.morphJoints as Set<string> | undefined;
  const normalizedClip = bvh.clip.clone();
  normalizedClip.tracks = normalizedClip.tracks.filter((track) => {
    if (morphJoints && isMorphTrack(track.name, morphJoints)) return false;
    return (
      track.name.includes('quaternion') ||
      (rootPositionTrackName != null && track.name === rootPositionTrackName)
    );
  });

  (
    normalizedClip as unknown as { userData?: Record<string, unknown> }
  ).userData = userData;

  return {
    skeleton: bvh.skeleton.clone(),
    clip: normalizedClip,
  };
}

export async function loadBVHAnimation(
  bvhUrl: string,
  rotationOffsets?: RotationOffsets,
) {
  const [resolved] = await loadBVHFiles([bvhUrl], rotationOffsets);
  return resolved ? normalizeBVHForPlayback(resolved) : null;
}

export async function loadBVHAnimations(
  bvhUrls: string[],
  rotationOffsets?: RotationOffsets,
) {
  const resolved = await loadBVHFiles(bvhUrls, rotationOffsets);
  return resolved.map(normalizeBVHForPlayback);
}

export function useBVHAnimations({
  bvhUrls,
  reloadKey,
  rotationOffsets,
}: UseBVHAnimationsConfig) {
  const [loadedBvhs, setLoadedBvhs] = useState<BVH[]>([]);
  const [loadedBvhUrls, setLoadedBvhUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const bvhUrlsRef = useRef(bvhUrls);
  bvhUrlsRef.current = bvhUrls;
  const bvhLoadIdentity = useMemo(
    () => `${buildBvhUrlsIdentity(bvhUrls)}\0${reloadKey ?? ''}`,
    [bvhUrls, reloadKey],
  );

  useEffect(() => {
    let isMounted = true;
    const urls = bvhUrlsRef.current;

    if (urls.length === 0) {
      setLoadedBvhs([]);
      setLoadedBvhUrls([]);
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setLoadedBvhs([]);
    setLoadedBvhUrls([]);
    setIsLoading(true);
    setError(null);

    const load = async () => {
      try {
        const resolved = await loadBVHAnimations(urls, rotationOffsets);
        if (!isMounted) return;
        setLoadedBvhs(resolved);
        setLoadedBvhUrls([...urls]);
      } catch (loadError) {
        if (!isMounted) return;
        setLoadedBvhs([]);
        setLoadedBvhUrls([]);
        setError(
          loadError instanceof Error
            ? loadError
            : new Error('Failed to load BVH animation data.'),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [bvhLoadIdentity, rotationOffsets]);

  return { bvhUrls: loadedBvhUrls, bvhs: loadedBvhs, isLoading, error };
}

export function useBVHAnimation({
  bvh,
  bvhUrl,
  rotationOffsets,
}: UseBVHAnimationConfig) {
  const [loadedBvh, setLoadedBvh] = useState<BVH | null>(() =>
    bvh ? normalizeBVHForPlayback(bvh) : null,
  );
  const [loadedBvhUrl, setLoadedBvhUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (bvh) {
      setLoadedBvh(normalizeBVHForPlayback(bvh));
      setLoadedBvhUrl(null);
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (!bvhUrl) {
      setLoadedBvh(null);
      setLoadedBvhUrl(null);
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resolved = await loadBVHAnimation(bvhUrl, rotationOffsets);
        if (!isMounted) return;
        setLoadedBvh(resolved ?? null);
        setLoadedBvhUrl(resolved ? bvhUrl : null);
      } catch (loadError) {
        if (!isMounted) return;
        setLoadedBvh(null);
        setLoadedBvhUrl(null);
        setError(
          loadError instanceof Error
            ? loadError
            : new Error('Failed to load BVH animation data.'),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [bvh, bvhUrl, rotationOffsets]);

  return { bvh: loadedBvh, bvhUrl: loadedBvhUrl, isLoading, error };
}
